import React, { useEffect, useState, useRef, useCallback } from 'react';
import CreditService from '../services/creditService';
import './css/CreditManagement.css';
import OrgSetupWizard from '../components/OrgSetupWizard';
import AdminLoginModal from '../components/AdminLoginModal';

// Enhanced spinner component
const Spinner = () => <div className="spinner" />;

// Toast notification component
const Toast = ({ message, type, onClose }) => (
  <div className={`toast ${type}`}>
    <span className="toast-icon">
      {type === 'success' && 'SUCCESS'}
      {type === 'error' && 'ERROR'}
      {type === 'warning' && 'WARNING'}
    </span>
    <span className="toast-message">{message}</span>
    <button onClick={onClose} style={{marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer'}}>
      ×
    </button>
  </div>
);

// Searchable Select Component
const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select an option...", 
  searchPlaceholder = "Type to search...",
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const filtered = options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
    setHighlightedIndex(-1);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className={`searchable-select ${className}`} ref={dropdownRef}>
      <div 
        className={`select-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
      >
        <span className="select-value">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="select-arrow">
          {isOpen ? '▲' : '▼'}
        </span>
      </div>
      
      {isOpen && (
        <div className="select-dropdown">
          <input
            ref={inputRef}
            type="text"
            className="select-search"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
          <div className="select-options">
            {filteredOptions.length === 0 ? (
              <div className="select-option no-options">No options found</div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={`select-option ${index === highlightedIndex ? 'highlighted' : ''} ${option.value === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Status indicator component
const StatusIndicator = ({ subscriptionType, trialEndDate }) => {
  const isTrialExpired = trialEndDate && new Date(trialEndDate) < new Date();
  
  if (subscriptionType === 'paid') {
    return <span className="status-indicator active">Paid</span>;
  }
  
  if (isTrialExpired) {
    return <span className="status-indicator expired">Expired</span>;
  }
  
  return <span className="status-indicator trial">Trial</span>;
};

const CreditManagement = () => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [uploadCreditAmount, setUploadCreditAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgDomain, setOrgDomain] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState('member');
  const [creditsToRemove, setCreditsToRemove] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState(null);
  const [planType, setPlanType] = useState('free_trial');
  const [trialDays, setTrialDays] = useState('14');
  const [trialEnd, setTrialEnd] = useState('');
  const [switchPlanType, setSwitchPlanType] = useState('paid');
  const [switchTrialDays, setSwitchTrialDays] = useState('14');
  const [switchTrialEnd, setSwitchTrialEnd] = useState('');
  const [creationMsg, setCreationMsg] = useState('');
  const emailInputRef = useRef(null);
  const creditInputRef = useRef(null);
  const [showWizard, setShowWizard] = useState(false);
  const [authenticated, setAuthenticated] = useState(!!localStorage.getItem('adminJwt'));
  
  // Enhanced state for better UX
  const [searchEmail, setSearchEmail] = useState('');
  const [searchOrgName, setSearchOrgName] = useState('');
  const [showPaidOnly, setShowPaidOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [serverTotal, setServerTotal] = useState(0);
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [editOrgName, setEditOrgName] = useState('');
  const [operationEmail, setOperationEmail] = useState('');
  const [toasts, setToasts] = useState([]);
  const [buttonLoadingStates, setButtonLoadingStates] = useState({});
  const [uploadCreditsToRemove, setUploadCreditsToRemove] = useState('');
  const [updateTrialEnd, setUpdateTrialEnd] = useState('');
  const [fileTransferFileId, setFileTransferFileId] = useState('');
  const [fileTransferNewEmail, setFileTransferNewEmail] = useState('');
  const [blockUserEmail, setBlockUserEmail] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [userBlockStatus, setUserBlockStatus] = useState(null);

  // When search/filter changes, reset to page 1
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchEmail, searchOrgName, showPaidOnly]);

  // Fetch organizations with useCallback to avoid dependency issues
  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await CreditService.getOrganizationsOverview({ 
        page: currentPage, 
        limit: itemsPerPage, 
        includeUsers: true,
        searchEmail: searchEmail.trim(),
        searchOrgName: searchOrgName.trim(),
        showPaidOnly
      });
      setOrganizations(data.organizations || []);
      setServerTotal(data.total || 0);
    } catch (err) {
      console.error(err);
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchEmail, searchOrgName, showPaidOnly]);

  // Fetch when dependencies change
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Prepare options for SearchableSelect
  const organizationOptions = organizations.map(org => ({
    value: org.id,
    label: `${org.name} (DL: ${org.credits_available || 0}, UL: ${org.upload_credits_available || 0})`
  }));

  const roleOptions = [
    { value: 'member', label: 'Member' },
    { value: 'admin', label: 'Admin' },
    { value: 'owner', label: 'Owner' }
  ];

  const planOptions = [
    { value: 'free_trial', label: 'Free Trial' },
    { value: 'paid', label: 'Paid' }
  ];

  // Enhanced Toast management with detailed error handling
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  };

  // Enhanced error message handler
  const getErrorMessage = (error) => {
    // Handle different error response structures
    const errorData = error.response?.data;
    
    if (errorData?.errorCode) {
      const { errorCode, message, organizationId } = errorData;
      
      switch (errorCode) {
        case 'USER_HAS_ORGANIZATION':
          return `User already belongs to organization ID: ${organizationId}. Options: 1) Use "Move User" to transfer them, or 2) Allow multiple org membership by using the regular "Add User" function.`;
        case 'USER_NOT_FOUND':
          return 'User not found. Please check the email address.';
        case 'ORGANIZATION_NOT_FOUND':
          return 'Organization not found. Please select a valid organization.';
        case 'INSUFFICIENT_CREDITS':
          return 'Insufficient credits in the organization.';
        case 'USER_ALREADY_IN_ORG':
          return 'User is already a member of this organization.';
        case 'PERMISSION_DENIED':
          return 'Permission denied. You may not have the required permissions.';
        case 'CANNOT_REMOVE_OWNER':
          return 'Cannot remove organization owner directly. The backend requires force=true parameter for owner removal. This should not happen with the current interface - please contact support if this error persists.';
        case 'ORGANIZATION_NOT_EMPTY':
          return 'Cannot delete organization - it still contains users. Remove all users first.';
        case 'ORGANIZATION_HAS_CREDITS':
          return 'Cannot delete organization - it still has credits. Remove all credits first.';
        case 'CANNOT_DELETE_ORGANIZATION':
          return 'Organization cannot be deleted. It may have dependencies or restrictions.';
        default:
          return message || 'An unexpected error occurred.';
      }
    }
    
    // Handle standard error messages
    if (errorData?.error && typeof errorData.error === 'string') {
      // Check for specific error patterns in the message
      if (errorData.error.includes('already belongs to an organization')) {
        return `${errorData.error} Use "Move User" to transfer them to a different organization.`;
      }
      if (errorData.error.includes('organization not found') || errorData.error.includes('Organization not found')) {
        return 'Organization not found. It may have been deleted by another administrator.';
      }
      if (errorData.error.includes('cannot delete') || errorData.error.includes('Cannot delete')) {
        return `${errorData.error} The organization may have remaining data or restrictions preventing deletion.`;
      }
      if (errorData.error.includes('force') || errorData.error.includes('Force')) {
        return `Force operation failed: ${errorData.error} The backend may not support force transfer. Contact administrator for manual intervention.`;
      }
      return errorData.error;
    }
    
    return error.message || errorData?.message || 'An unexpected error occurred.';
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Button loading state management
  const setButtonLoading = (buttonId, loading) => {
    setButtonLoadingStates(prev => ({ ...prev, [buttonId]: loading }));
  };

  // Pagination - server-side
  const currentOrganizations = organizations; 
  const totalPages = Math.ceil(serverTotal / itemsPerPage);

  const handleRowClick = (orgId) => {
    setExpandedOrgId(expandedOrgId === orgId ? null : orgId);
  };

  const handleEditOrgName = async (orgId) => {
    if (!editOrgName.trim()) return;
    setButtonLoading(`edit-${orgId}`, true);
    try {
      await CreditService.updateOrganizationName(orgId, editOrgName);
      showToast('Organization name updated successfully');
      setEditingOrgId(null);
      setEditOrgName('');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading(`edit-${orgId}`, false);
    }
  };

  const handleAddCredits = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !creditAmount) return;
    setButtonLoading('add-credits', true);
    try {
      await CreditService.addCredits(selectedOrg, parseInt(creditAmount, 10));
      showToast(`Added ${creditAmount} download credits successfully`);
      setCreditAmount('');
      await fetchOrganizations();
    } catch (err) {
      console.error(err);
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('add-credits', false);
    }
  };

  const handleAddUploadCredits = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !uploadCreditAmount) return;
    setButtonLoading('add-upload-credits', true);
    try {
      await CreditService.addUploadCredits(selectedOrg, parseInt(uploadCreditAmount, 10));
      showToast(`Added ${uploadCreditAmount} upload credits successfully`);
      setUploadCreditAmount('');
      await fetchOrganizations();
    } catch (err) {
      console.error(err);
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('add-upload-credits', false);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    if (!orgName) return;
    setButtonLoading('create-org', true);
    try {
      const payload = { name: orgName, domain: orgDomain, subscriptionType: planType };
      if (planType === 'free_trial') {
        if (trialEnd) payload.trialEndDate = trialEnd;
        else payload.trialDays = parseInt(trialDays, 10);
      }
      const result = await CreditService.createOrganization(payload);
      const newOrgId = result?.organization?.id;
      showToast('Organization created successfully!');
      setCreationMsg('Organization created successfully — now add team members and initial credits.');
      if (newOrgId) setSelectedOrg(newOrgId);
      setOrgName('');
      setOrgDomain('');
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 0);
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('create-org', false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !userEmail) return;
    setButtonLoading('add-user', true);
    try {
      await CreditService.addUserToOrganization(selectedOrg, userEmail, userRole);
      showToast(`Added ${userEmail} to organization`);
      setUserEmail('');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('add-user', false);
    }
  };

  const handleAddUserByEmail = async (e) => {
    e.preventDefault();
    if (!operationEmail) return;
    
    if (!selectedOrg) {
      showToast('Please select a target organization first', 'warning');
      return;
    }
    
    const userOrgs = organizations.filter(org => 
      org.users?.some(user => user.email === operationEmail)
    );
    
    // Check if user is already in the target organization
    const isUserInTargetOrg = userOrgs.some(org => org.id === selectedOrg);
    if (isUserInTargetOrg) {
      showToast('User is already a member of the selected organization', 'warning');
      return;
    }
    
    setButtonLoading('add-user-email', true);
    try {
      await CreditService.addUserToOrganization(selectedOrg, operationEmail, userRole);
      
      if (userOrgs.length === 0) {
        showToast(`New user ${operationEmail} added to organization successfully`);
      } else {
        showToast(`User ${operationEmail} added to organization (now in multiple organizations)`);
      }
      
      setOperationEmail('');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('add-user-email', false);
    }
  };

  // New function to move user between organizations
  const handleMoveUserBetweenOrgs = async (e) => {
    e.preventDefault();
    if (!operationEmail || !selectedOrg) return;
    
    const userOrgs = organizations.filter(org => 
      org.users?.some(user => user.email === operationEmail)
    );
    
    if (userOrgs.length === 0) {
      showToast('User not found in any organization. Use "Add User" instead to add them to an organization.', 'warning');
      return;
    }
    
    const currentOrg = userOrgs[0]; // Assuming user is in first found org
    const user = currentOrg.users.find(u => u.email === operationEmail);
    const targetOrg = organizations.find(o => o.id === selectedOrg);
    
    if (!user) {
      showToast('User details not found in current organization', 'error');
      return;
    }
    
    if (currentOrg.id === selectedOrg) {
      showToast('User is already in the selected organization', 'warning');
      return;
    }
    
    const remainingUsersCount = (currentOrg?.users?.length || 1) - 1;
    const willDeleteSourceOrg = remainingUsersCount === 0;
    
    // Check if moving this user will delete the source organization
    let confirmationMessage = '';
    
    if (willDeleteSourceOrg) {
      confirmationMessage = `🗑️ CRITICAL WARNING: SOURCE ORGANIZATION WILL BE DELETED!\n\n` +
        `Moving ${operationEmail} from "${currentOrg.name}" to "${targetOrg?.name}" will:\n` +
        `• Remove the last user from "${currentOrg.name}"\n` +
        `• PERMANENTLY DELETE the organization "${currentOrg.name}"\n` +
        `• Delete all organization data, settings, and history\n` +
        `• Add the user as a ${userRole} to "${targetOrg?.name}"\n` +
        `• This action CANNOT be undone!\n\n` +
        `Are you absolutely certain you want to DELETE "${currentOrg.name}" and move the user?`;
    } else if (user.role === 'owner') {
      confirmationMessage = `👑 OWNER TRANSFER: Force transfer enabled\n\n` +
        `Moving OWNER ${operationEmail} from "${currentOrg.name}" to "${targetOrg?.name}" will:\n` +
        `• Use force transfer to remove ownership from "${currentOrg.name}"\n` +
        `• Add them as a ${userRole} to "${targetOrg?.name}"\n` +
        `• ${remainingUsersCount} user(s) will remain in "${currentOrg.name}"\n` +
        `• The system will handle ownership transition automatically\n\n` +
        `Proceed with owner transfer?`;
    } else {
      confirmationMessage = `Move ${operationEmail} from "${currentOrg.name}" to "${targetOrg?.name}"?\n` +
        `${remainingUsersCount} user(s) will remain in "${currentOrg.name}".`;
    }
    
    if (!window.confirm(confirmationMessage)) return;
    
    // Final confirmation for organization deletion
    if (willDeleteSourceOrg) {
      const finalConfirm = window.confirm(
        `FINAL CONFIRMATION:\n\n` +
        `TYPE "DELETE" to confirm permanent deletion of organization "${currentOrg.name}"`
      );
      if (!finalConfirm) return;
    }
    
    setButtonLoading('move-user', true);
    try {
      // Use force transfer for owners
      const isOwner = user.role === 'owner';
      await CreditService.moveUserBetweenOrganizations(operationEmail, currentOrg.id, selectedOrg, userRole, user.uid, isOwner);
      
      // If source organization is now empty, delete it
      if (willDeleteSourceOrg) {
        await CreditService.deleteOrganization(currentOrg.id);
        showToast(`User ${operationEmail} moved successfully. Organization "${currentOrg.name}" was deleted (became empty).`, 'warning');
      } else {
        const ownerNote = isOwner ? ' (Owner transferred)' : '';
        showToast(`User ${operationEmail} moved successfully from "${currentOrg.name}" to "${targetOrg?.name}"${ownerNote}`);
      }
      
      setOperationEmail('');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('move-user', false);
    }
  };

  // Enhanced function to remove user from current organization
  const handleRemoveUserFromCurrentOrg = async (e) => {
    e.preventDefault();
    if (!operationEmail) return;
    
    const userOrgs = organizations.filter(org => 
      org.users?.some(user => user.email === operationEmail)
    );
    
    if (userOrgs.length === 0) {
      showToast('User not found in any organization. Nothing to remove.', 'warning');
      return;
    }
    
    const currentOrg = userOrgs[0];
    const user = currentOrg.users.find(u => u.email === operationEmail);
    
    if (!user) {
      showToast('User details not found in current organization', 'error');
      return;
    }
    
    const remainingUsersCount = (currentOrg?.users?.length || 1) - 1;
    const willDeleteOrg = remainingUsersCount === 0;
    
    // Check if removing this user will delete the organization
    let confirmationMessage = '';
    
    if (willDeleteOrg) {
      confirmationMessage = `🗑️ CRITICAL WARNING: ORGANIZATION WILL BE DELETED!\n\n` +
        `Removing ${operationEmail} from "${currentOrg.name}" will:\n` +
        `• Remove the last user from the organization\n` +
        `• PERMANENTLY DELETE the entire organization\n` +
        `• Delete all organization data, settings, and history\n` +
        `• This action CANNOT be undone!\n\n` +
        `Are you absolutely certain you want to DELETE the organization "${currentOrg.name}"?`;
    } else if (user.role === 'owner') {
      confirmationMessage = `👑 FORCE REMOVE OWNER: Enhanced removal enabled\n\n` +
        `Removing OWNER ${operationEmail} from "${currentOrg.name}" will:\n` +
        `• Use force removal to bypass ownership restrictions\n` +
        `• Remove their ownership from "${currentOrg.name}"\n` +
        `• ${remainingUsersCount} user(s) will remain in the organization\n` +
        `• The system will handle ownership transition automatically\n\n` +
        `Proceed with force owner removal?`;
    } else {
      confirmationMessage = `Remove ${operationEmail} from "${currentOrg.name}"?\n` +
        `${remainingUsersCount} user(s) will remain in the organization.`;
    }
    
    if (!window.confirm(confirmationMessage)) return;
    
    // Final confirmation for organization deletion
    if (willDeleteOrg) {
      const finalConfirm = window.confirm(
        `FINAL CONFIRMATION:\n\n` +
        `TYPE "DELETE" to confirm permanent deletion of organization "${currentOrg.name}"`
      );
      if (!finalConfirm) return;
    }
    
    setButtonLoading('remove-user-email', true);
    try {
      // Use force removal for owners
      const isOwner = user.role === 'owner';
      if (isOwner) {
        await CreditService.forceRemoveUserFromOrganization(currentOrg.id, user.uid, true);
      } else {
        await CreditService.removeUserFromOrganization(currentOrg.id, user.uid);
      }
      
      // If organization is now empty, delete it
      if (willDeleteOrg) {
        await CreditService.deleteOrganization(currentOrg.id);
        showToast(`Organization "${currentOrg.name}" deleted successfully (was empty after removing ${operationEmail})`, 'warning');
      } else {
        const ownerNote = isOwner ? ' (Owner removed)' : '';
        showToast(`User ${operationEmail} removed from ${currentOrg.name}${ownerNote}`);
      }
      
      setOperationEmail('');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('remove-user-email', false);
    }
  };

  const handleRemoveCredits = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !creditsToRemove) return;
    setButtonLoading('remove-credits', true);
    try {
      await CreditService.removeCredits(selectedOrg, parseInt(creditsToRemove, 10));
      showToast(`Removed ${creditsToRemove} credits successfully`);
      setCreditsToRemove('');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('remove-credits', false);
    }
  };

  const handleRemoveUploadCredits = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !uploadCreditsToRemove) return;
    setButtonLoading('remove-upload-credits', true);
    try {
      await CreditService.removeUploadCredits(selectedOrg, parseInt(uploadCreditsToRemove, 10));
      showToast(`Removed ${uploadCreditsToRemove} upload credits successfully`);
      setUploadCreditsToRemove('');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('remove-upload-credits', false);
    }
  };

  const handleRemoveUser = async (orgId, user) => {
    const organization = organizations.find(org => org.id === orgId);
    const organizationName = organization?.name || 'Unknown Organization';
    const userEmail = user.email || user.display_name || 'Unknown User';
    const remainingUsersCount = (organization?.users?.length || 1) - 1;
    const willDeleteOrg = remainingUsersCount === 0;
    
    // Check if removing this user will delete the organization
    let confirmationMessage = '';
    
    if (willDeleteOrg) {
      confirmationMessage = `🗑️ CRITICAL WARNING: ORGANIZATION WILL BE DELETED!\n\n` +
        `Removing ${userEmail} from "${organizationName}" will:\n` +
        `• Remove the last user from the organization\n` +
        `• PERMANENTLY DELETE the entire organization\n` +
        `• Delete all organization data, settings, and history\n` +
        `• This action CANNOT be undone!\n\n` +
        `Are you absolutely certain you want to DELETE the organization "${organizationName}"?`;
    } else if (user.role === 'owner') {
      confirmationMessage = `👑 FORCE REMOVE OWNER: Enhanced removal enabled\n\n` +
        `Removing OWNER ${userEmail} from "${organizationName}" will:\n` +
        `• Use force removal to bypass ownership restrictions\n` +
        `• Remove their ownership from "${organizationName}"\n` +
        `• ${remainingUsersCount} user(s) will remain in the organization\n` +
        `• The system will handle ownership transition automatically\n\n` +
        `Proceed with force owner removal?`;
    } else {
      confirmationMessage = `Remove ${userEmail} from "${organizationName}"?\n` +
        `${remainingUsersCount} user(s) will remain in the organization.`;
    }
    
    if (!window.confirm(confirmationMessage)) return;
    
    // Final confirmation for organization deletion
    if (willDeleteOrg) {
      const finalConfirm = window.confirm(
        `FINAL CONFIRMATION:\n\n` +
        `TYPE "DELETE" to confirm permanent deletion of organization "${organizationName}"`
      );
      if (!finalConfirm) return;
    }
    
    setButtonLoading(`remove-${user.uid}`, true);
    try {
      // Use force removal for owners
      const isOwner = user.role === 'owner';
      if (isOwner) {
        await CreditService.forceRemoveUserFromOrganization(orgId, user.uid, true);
      } else {
      await CreditService.removeUserFromOrganization(orgId, user.uid);
      }
      
      // If organization is now empty, delete it
      if (willDeleteOrg) {
        await CreditService.deleteOrganization(orgId);
        showToast(`Organization "${organizationName}" deleted successfully (was empty after removing ${userEmail})`, 'warning');
      } else {
        const ownerNote = isOwner ? ' (Owner removed)' : '';
        showToast(`User ${userEmail} removed successfully from ${organizationName}${ownerNote}`);
      }
      
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading(`remove-${user.uid}`, false);
    }
  };

  const handleSwitchPlan = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setButtonLoading('switch-plan', true);
    try {
      const payload = { subscriptionType: switchPlanType };
      if (switchPlanType === 'free_trial') {
        if (switchTrialEnd) payload.trialEndDate = switchTrialEnd;
        else payload.trialDays = parseInt(switchTrialDays, 10);
      }
      await CreditService.switchPlan(selectedOrg, payload);
      showToast('Plan updated successfully');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('switch-plan', false);
    }
  };

  const handleUpdateTrialExpiry = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !updateTrialEnd) return;
    setButtonLoading('update-trial-expiry', true);
    try {
      await CreditService.updateTrialExpiry(selectedOrg, updateTrialEnd);
      showToast('Trial expiry updated successfully');
      setUpdateTrialEnd('');
      await fetchOrganizations();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('update-trial-expiry', false);
    }
  };

  const handleLoginSuccess = () => { 
    setAuthenticated(true); 
    fetchOrganizations(); 
  };

  const clearSearch = () => {
    setSearchEmail('');
    setSearchOrgName('');
    setShowPaidOnly(false);
    setCurrentPage(1);
  };

  const handleFileTransfer = async (e) => {
    e.preventDefault();
    if (!fileTransferFileId || !fileTransferNewEmail) {
      showToast('Please fill in file ID and destination email for file transfer', 'warning');
      return;
    }

    setButtonLoading('file-transfer', true);
    try {
      await CreditService.transferFileOwnership(null, fileTransferFileId, fileTransferNewEmail);
      
      showToast(`File ${fileTransferFileId} assigned to ${fileTransferNewEmail} successfully`);
      setFileTransferFileId('');
      setFileTransferNewEmail('');
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('file-transfer', false);
    }
  };

  const handleCheckBlockStatus = async () => {
    if (!blockUserEmail) {
      showToast('Please enter an email to check', 'warning');
      return;
    }
    setButtonLoading('check-block-status', true);
    try {
      const status = await CreditService.getUserBlockStatus(blockUserEmail);
      setUserBlockStatus(status);
      if (status.isBlocked) {
        showToast(`User ${blockUserEmail} is BLOCKED. Reason: ${status.reason || 'No reason provided'}`, 'warning');
      } else {
        showToast(`User ${blockUserEmail} is NOT blocked`, 'success');
      }
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
      setUserBlockStatus(null);
    } finally {
      setButtonLoading('check-block-status', false);
    }
  };

  const handleBlockUser = async (e) => {
    e.preventDefault();
    if (!blockUserEmail) {
      showToast('Please enter an email to block', 'warning');
      return;
    }

    if (!window.confirm(`⛔ Are you sure you want to BLOCK user "${blockUserEmail}"?\n\nThis will prevent them from logging in and using PolarCut.`)) {
      return;
    }

    setButtonLoading('block-user', true);
    try {
      await CreditService.blockUser(blockUserEmail, blockReason || 'Blocked by admin');
      showToast(`User ${blockUserEmail} has been BLOCKED`, 'warning');
      setBlockReason('');
      setUserBlockStatus({ isBlocked: true, reason: blockReason || 'Blocked by admin' });
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('block-user', false);
    }
  };

  const handleUnblockUser = async (e) => {
    e.preventDefault();
    if (!blockUserEmail) {
      showToast('Please enter an email to unblock', 'warning');
      return;
    }

    if (!window.confirm(`✅ Are you sure you want to UNBLOCK user "${blockUserEmail}"?\n\nThis will restore their access to PolarCut.`)) {
      return;
    }

    setButtonLoading('unblock-user', true);
    try {
      await CreditService.unblockUser(blockUserEmail);
      showToast(`User ${blockUserEmail} has been UNBLOCKED`, 'success');
      setUserBlockStatus({ isBlocked: false });
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setButtonLoading('unblock-user', false);
    }
  };

  if (!authenticated) {
    return <AdminLoginModal onSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="credit-mgmt-grid">
      {/* Toast Container */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* LEFT PANE – SEARCH & OVERVIEW */}
      <div className="left-pane card fade-in">
        <div className="pane-header">
          <h2>Organizations ({serverTotal})</h2>
          <button 
            className="primary" 
            onClick={fetchOrganizations} 
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        
        {/* Enhanced Search Controls */}
        <div className="search-controls">
          <div style={{display: 'flex', gap: '0.5rem', marginBottom: '0.5rem'}}>
            <input
              type="email"
              placeholder="Search by user email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              style={{flex: 1}}
            />
            {/* <input
              type="text"
              placeholder="Search by org name..."
              value={searchOrgName}
              onChange={(e) => setSearchOrgName(e.target.value)}
              style={{flex: 1}}
            /> */}
            <button onClick={clearSearch} className="secondary">
              Clear
            </button>
          </div>
          
          {/* Simple Toggle for Paid Organizations */}
          <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={showPaidOnly}
                onChange={(e) => setShowPaidOnly(e.target.checked)}
                style={{margin: 0}}
              />
              <span style={{fontSize: '0.9rem', fontWeight: '500'}}>Show paid organizations only</span>
            </label>
          </div>
          
          <small style={{color: 'var(--text-secondary)'}}>
            Showing <strong>{organizations.length}</strong> of <strong>{serverTotal}</strong> organizations
            {(searchEmail || searchOrgName || showPaidOnly) && <span style={{color: 'var(--primary-color)', fontWeight: '600'}}> (Filtered)</span>}
          </small>
        </div>

        {loading ? (
          <div style={{textAlign: 'center', padding: '2rem'}}>
          <Spinner />
            <p style={{marginTop: '1rem', color: 'var(--text-secondary)'}}>Loading organizations...</p>
          </div>
        ) : (
          <>
            <div className="table-container">
          <table className="org-table">
            <thead>
              <tr>
                <th>Name</th>
                    <th>Download Credits</th>
                    <th>Upload Credits</th>
                    <th>Users</th>
                    <th>Status</th>
                    <th>Actions</th>
              </tr>
            </thead>
            <tbody>
                  {currentOrganizations.map((org) => (
                <React.Fragment key={org.id}>
                  <tr
                        className={`${expandedOrgId === org.id ? 'active-row' : ''} ${selectedOrg === org.id ? 'selected-row' : ''}`}
                    onClick={() => handleRowClick(org.id)}
                  >
                        <td>
                          {editingOrgId === org.id ? (
                            <div className="edit-name-container">
                              <input
                                type="text"
                                value={editOrgName}
                                onChange={(e) => setEditOrgName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleEditOrgName(org.id)}
                                className="edit-name-input"
                                autoFocus
                              />
                              <button 
                                onClick={() => handleEditOrgName(org.id)}
                                className="primary small"
                                disabled={buttonLoadingStates[`edit-${org.id}`]}
                              >
                                {buttonLoadingStates[`edit-${org.id}`] ? '...' : 'Save'}
                              </button>
                              <button 
                                onClick={() => setEditingOrgId(null)}
                                className="small secondary"
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                              <span style={{fontWeight: '600'}}>{org.name}</span>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingOrgId(org.id);
                                  setEditOrgName(org.name);
                                }}
                                className="edit-btn"
                                title="Edit name"
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${(org.credits_available ?? 0) > 50 ? 'green' : 'warning'}`}>
                            {org.credits_available ?? 0}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${(org.upload_credits_available ?? 0) > 5 ? 'green' : 'warning'}`}>
                            {org.upload_credits_available ?? 0}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            color: (org.users?.length ?? 0) === 1 ? '#dc2626' : 'var(--text-secondary)',
                            fontWeight: (org.users?.length ?? 0) === 1 ? '700' : 'normal'
                          }}>
                            {org.users?.length ?? 0}
                          </span>
                          {(org.users?.length ?? 0) === 1 && (
                            <div style={{fontSize: '0.7rem', color: '#dc2626', fontWeight: '600'}}>
                              LAST USER
                            </div>
                          )}
                        </td>
                        <td>
                          <StatusIndicator 
                            subscriptionType={org.subscription_type}
                            trialEndDate={org.trial_end_date}
                          />
                        </td>
                        <td>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrg(org.id);
                              showToast(`Selected: ${org.name}`);
                            }}
                            className={`small ${selectedOrg === org.id ? 'primary' : 'secondary'}`}
                          >
                            {selectedOrg === org.id ? 'Selected' : 'Select'}
                          </button>
                        </td>
                  </tr>
                  {expandedOrgId === org.id && (
                    <tr className="user-row">
                          <td colSpan="6">
                            <div style={{padding: '1rem'}}>
                              <h5 style={{margin: '0 0 0.75rem 0', color: 'var(--text-primary)'}}>
                                Users ({org.users?.length || 0})
                              </h5>
                        {org.users && org.users.length > 0 ? (
                                <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem'}}>
                            {org.users.map((u) => (
                                    <div key={u.uid} className={`user-tag ${u.role === 'owner' ? 'owner-tag' : ''}`}>
                                      <span>{u.email}</span>
                                      <span className={`role ${u.role === 'owner' ? 'owner-role' : ''}`}>
                                        {u.role === 'owner' ? 'OWNER' : (u.role || 'member')}
                                      </span>
                                      <button 
                                        className="danger small"
                                        onClick={() => handleRemoveUser(org.id, u)}
                                        disabled={buttonLoadingStates[`remove-${u.uid}`]}
                                        title={u.role === 'owner' ? "Force Remove OWNER (will use force transfer)" : "Remove user"}
                                      >
                                        {buttonLoadingStates[`remove-${u.uid}`] ? '...' : '×'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p style={{fontSize: '0.9rem', color: 'var(--text-secondary)', margin: '0.5rem 0'}}>
                                  No users found.
                                </p>
                              )}
                              <div style={{
                                padding: '0.75rem', 
                                background: 'var(--secondary-color)', 
                                borderRadius: 'var(--radius)',
                                fontSize: '0.85rem', 
                                color: 'var(--text-secondary)'
                              }}>
                                <div style={{marginBottom: '0.25rem'}}>
                                  <strong>Download:</strong> {org.credits_available || 0} available, {org.credits_used || 0} used
                                </div>
                                <div style={{marginBottom: '0.25rem'}}>
                                  <strong>Upload:</strong> {org.upload_credits_available || 0} available, {org.upload_credits_used || 0} used
                                </div>
                                {org.trial_end_date && (
                                  <div style={{fontSize: '0.8rem', color: 'var(--text-secondary)'}}>
                                    Trial ends: <strong>{new Date(org.trial_end_date).toLocaleDateString()}</strong>
                                  </div>
                                )}
                              </div>
                            </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
            </div>
            
            {/* Enhanced Pagination */}
            <div className="pagination">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span style={{padding: '0.5rem 1rem', fontWeight: '600'}}>
                Page {currentPage} of {Math.max(totalPages, 1)}
              </span>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.max(totalPages, 1)))}
                disabled={currentPage >= Math.max(totalPages, 1)}
              >
                Next
              </button>
              <select 
                value={itemsPerPage}
                onChange={(e) => { setCurrentPage(1); setItemsPerPage(parseInt(e.target.value, 10)); }}
                style={{ marginLeft: '1rem' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* RIGHT PANE – ACTIONS */}
      <div className="right-pane">
        <button 
          className="cta-add-org" 
          onClick={() => setShowWizard(true)}
        >
          Add New Organization
        </button>
        
        {showWizard && (
          <OrgSetupWizard 
            onClose={() => setShowWizard(false)} 
            onComplete={() => {
              fetchOrganizations();
              setShowWizard(false);
              showToast('Organization setup completed!');
            }} 
          />
        )}
        
        {creationMsg && (
          <div className="toast success" style={{position: 'relative', marginBottom: '1rem'}}>
            <span className="toast-icon">SUCCESS</span>
            <span className="toast-message">{creationMsg}</span>
            <button onClick={() => setCreationMsg('')} style={{marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer'}}>
              ×
            </button>
          </div>
        )}

        {selectedOrg && (
          <div className="selected-org-info">
            <strong>Selected:</strong> {organizations.find(o => o.id === selectedOrg)?.name}
          </div>
        )}

        {/* EMAIL-BASED USER OPERATIONS */}
        <div className="card form-card fade-in">
          <h3>Find & Manage User by Email</h3>
          <form onSubmit={handleAddUserByEmail}>
            <label>
              User Email
              <input 
                type="email" 
                value={operationEmail} 
                onChange={(e) => setOperationEmail(e.target.value)}
                placeholder="Enter user email to find their orgs..."
              />
            </label>
            {operationEmail && (
              <div style={{fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem', padding: '0.5rem', background: 'var(--secondary-color)', borderRadius: 'var(--radius)'}}>
                <div style={{marginBottom: '0.25rem'}}>
                  <strong>Current Organizations:</strong>
                </div>
                {(() => {
                  const userOrgs = organizations.filter(org => 
                    org.users?.some(user => user.email === operationEmail)
                  );
                  
                  if (userOrgs.length === 0) {
                    return <span style={{color: 'var(--text-secondary)'}}>User not found in any organization (ready to add to new org)</span>;
                  }
                  
                  return userOrgs.map(org => {
                    const user = org.users.find(u => u.email === operationEmail);
                    return (
                      <div key={org.id} style={{marginBottom: '0.25rem'}}>
                        <span style={{fontWeight: '600'}}>{org.name}</span> 
                        <span style={{color: 'var(--text-secondary)', fontSize: '0.8rem'}}> (Role: {user?.role || 'member'})</span>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
            <label>
              Target Organization
              <SearchableSelect
                options={organizationOptions}
                value={selectedOrg}
                onChange={setSelectedOrg}
                placeholder="Select organization..."
                searchPlaceholder="Search organizations..."
              />
            </label>
            <label>
              Role
              <SearchableSelect
                options={roleOptions}
                value={userRole}
                onChange={setUserRole}
                placeholder="Select role..."
                searchPlaceholder="Search roles..."
              />
            </label>
            <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
              <button
                className="primary"
                type="submit"
                disabled={buttonLoadingStates['add-user-email'] || !operationEmail || !selectedOrg}
                title="Add user to the selected organization (user can be in multiple orgs) - Works for all user levels including owners"
              >
                {buttonLoadingStates['add-user-email'] ? 'Adding User...' : 'Add User to Selected Org'}
              </button>
              
              <button
                className="warning"
                type="button"
                onClick={handleMoveUserBetweenOrgs}
                disabled={buttonLoadingStates['move-user'] || !operationEmail || !selectedOrg}
                title="Transfer user from current org to selected org - Supports all hierarchical levels including owners with force transfer"
              >
                {buttonLoadingStates['move-user'] ? 'Moving User...' : 'Move User to Selected Org'}
              </button>
              
              <button
                className="danger secondary"
                type="button"
                onClick={handleRemoveUserFromCurrentOrg}
                disabled={buttonLoadingStates['remove-user-email'] || !operationEmail}
                title="Remove user from their current organization completely - Supports force removal of owners"
              >
                {buttonLoadingStates['remove-user-email'] ? 'Removing User...' : 'Remove from Current Org'}
              </button>
            </div>
          </form>
        </div>

        {/* ADD CREDITS - Collapsed by default */}
        <details className="card form-card fade-in">
          <summary style={{cursor: 'pointer', fontWeight: 'bold', padding: '0.5rem 0'}}>
            Add Credits
          </summary>
          <form onSubmit={handleAddCredits}>
            <label>
              Organization
              <SearchableSelect
                options={organizationOptions}
                value={selectedOrg}
                onChange={setSelectedOrg}
                placeholder="Select organization..."
                searchPlaceholder="Search organizations..."
              />
            </label>
            <label>
              Download Credits to Add
              <input
                type="number"
                min="1"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="Enter number of download credits to add"
              />
            </label>
            <button 
              className="primary" 
              type="submit" 
              disabled={buttonLoadingStates['add-credits'] || !creditAmount || !selectedOrg}
            >
              {buttonLoadingStates['add-credits'] ? 'Adding Credits...' : 'Add Download Credits'}
            </button>
          </form>
        </details>

        {/* ADD UPLOAD CREDITS - Collapsed by default */}
        <details className="card form-card fade-in">
          <summary style={{cursor: 'pointer', fontWeight: 'bold', padding: '0.5rem 0'}}>
            Add Upload Credits
          </summary>
          <form onSubmit={handleAddUploadCredits}>
            <label>
              Organization
              <SearchableSelect
                options={organizationOptions}
                value={selectedOrg}
                onChange={setSelectedOrg}
                placeholder="Select organization..."
                searchPlaceholder="Search organizations..."
              />
            </label>
            <label>
              Upload Credits to Add
              <input
                type="number"
                min="1"
                value={uploadCreditAmount}
                onChange={(e) => setUploadCreditAmount(e.target.value)}
                placeholder="Enter number of upload credits to add"
              />
            </label>
            <button 
              className="primary" 
              type="submit" 
              disabled={buttonLoadingStates['add-upload-credits'] || !uploadCreditAmount || !selectedOrg}
            >
              {buttonLoadingStates['add-upload-credits'] ? 'Adding Upload Credits...' : 'Add Upload Credits'}
            </button>
          </form>
        </details>

        {/* UPLOAD CREDITS MANAGEMENT - Collapsed by default */}
        <details className="card form-card fade-in">
          <summary style={{cursor: 'pointer', fontWeight: 'bold', padding: '0.5rem 0'}}>
            📤 Upload Credits Management
          </summary>
          
          {/* Add Upload Credits */}
          <div style={{marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)'}}>
            <h4 style={{margin: '0 0 1rem 0', color: 'var(--success-color)'}}>➕ Add Upload Credits</h4>
            <form onSubmit={handleAddUploadCredits}>
              <label>
                Organization
                <SearchableSelect
                  options={organizationOptions}
                  value={selectedOrg}
                  onChange={setSelectedOrg}
                  placeholder="Select organization..."
                  searchPlaceholder="Search organizations..."
                />
              </label>
              <label>
                Upload Credits to Add
                <input
                  type="number"
                  min="1"
                  value={uploadCreditAmount}
                  onChange={(e) => setUploadCreditAmount(e.target.value)}
                  placeholder="Enter number of upload credits to add"
                />
              </label>
              <button 
                className="primary" 
                type="submit" 
                disabled={buttonLoadingStates['add-upload-credits'] || !uploadCreditAmount || !selectedOrg}
              >
                {buttonLoadingStates['add-upload-credits'] ? 'Adding Upload Credits...' : 'Add Upload Credits'}
              </button>
            </form>
          </div>

          {/* Remove Upload Credits */}
          <div style={{padding: '1rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)'}}>
            <h4 style={{margin: '0 0 1rem 0', color: 'var(--danger-color)'}}>➖ Deduct Upload Credits</h4>
            <form onSubmit={handleRemoveUploadCredits}>
              <label>
                Organization
                <SearchableSelect
                  options={organizationOptions}
                  value={selectedOrg}
                  onChange={setSelectedOrg}
                  placeholder="Select organization..."
                  searchPlaceholder="Search organizations..."
                />
              </label>
              <label>
                Upload Credits to Remove
                <input
                  type="number"
                  min="1"
                  value={uploadCreditsToRemove}
                  onChange={(e) => setUploadCreditsToRemove(e.target.value)}
                  placeholder="Enter number of upload credits to remove"
                />
              </label>
              <button 
                className="primary warning" 
                type="submit" 
                disabled={buttonLoadingStates['remove-upload-credits'] || !uploadCreditsToRemove || !selectedOrg}
              >
                {buttonLoadingStates['remove-upload-credits'] ? 'Removing Upload Credits...' : 'Remove Upload Credits'}
              </button>
            </form>
          </div>
        </details>

        {/* DOWNLOAD CREDITS MANAGEMENT - Collapsed by default */}
        <details className="card form-card fade-in">
          <summary style={{cursor: 'pointer', fontWeight: 'bold', padding: '0.5rem 0'}}>
            📥 Download Credits Management
          </summary>
          
          {/* Add Download Credits */}
          <div style={{marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)'}}>
            <h4 style={{margin: '0 0 1rem 0', color: 'var(--success-color)'}}>➕ Add Download Credits</h4>
            <form onSubmit={handleAddCredits}>
              <label>
                Organization
                <SearchableSelect
                  options={organizationOptions}
                  value={selectedOrg}
                  onChange={setSelectedOrg}
                  placeholder="Select organization..."
                  searchPlaceholder="Search organizations..."
                />
              </label>
              <label>
                Download Credits to Add
                <input
                  type="number"
                  min="1"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="Enter number of download credits to add"
                />
              </label>
              <button 
                className="primary" 
                type="submit" 
                disabled={buttonLoadingStates['add-credits'] || !creditAmount || !selectedOrg}
              >
                {buttonLoadingStates['add-credits'] ? 'Adding Credits...' : 'Add Download Credits'}
              </button>
            </form>
          </div>

          {/* Remove Download Credits */}
          <div style={{padding: '1rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)'}}>
            <h4 style={{margin: '0 0 1rem 0', color: 'var(--danger-color)'}}>➖ Deduct Download Credits</h4>
            <form onSubmit={handleRemoveCredits}>
              <label>
                Organization
                <SearchableSelect
                  options={organizationOptions}
                  value={selectedOrg}
                  onChange={setSelectedOrg}
                  placeholder="Select organization..."
                  searchPlaceholder="Search organizations..."
                />
              </label>
              <label>
                Download Credits to Remove
                <input
                  type="number"
                  min="1"
                  value={creditsToRemove}
                  onChange={(e) => setCreditsToRemove(e.target.value)}
                  placeholder="Enter number of download credits to remove"
                />
              </label>
              <button 
                className="primary warning" 
                type="submit" 
                disabled={buttonLoadingStates['remove-credits'] || !creditsToRemove || !selectedOrg}
              >
                {buttonLoadingStates['remove-credits'] ? 'Removing Credits...' : 'Remove Download Credits'}
              </button>
            </form>
          </div>
        </details>

        {/* TRIAL EXPIRY MANAGEMENT */}
        <div className="card form-card fade-in">
          <h3>Update Trial Expiry Date</h3>
          <form onSubmit={handleUpdateTrialExpiry}>
            <label>
              Organization
              <SearchableSelect
                options={organizationOptions}
                value={selectedOrg}
                onChange={setSelectedOrg}
                placeholder="Select organization..."
                searchPlaceholder="Search organizations..."
              />
            </label>
            <label>
              New Trial End Date
              <input
                type="date"
                value={updateTrialEnd}
                onChange={(e) => setUpdateTrialEnd(e.target.value)}
              />
            </label>
            <button 
              className="primary" 
              type="submit" 
              disabled={buttonLoadingStates['update-trial-expiry'] || !updateTrialEnd || !selectedOrg}
            >
              {buttonLoadingStates['update-trial-expiry'] ? 'Updating Trial Expiry...' : 'Update Trial Expiry'}
            </button>
          </form>
        </div>

        {/* SWITCH PLAN - Collapsed by default */}
        <details className="card form-card fade-in">
          <summary style={{cursor: 'pointer', fontWeight: 'bold', padding: '0.5rem 0'}}>
            Switch Plan
          </summary>
          <form onSubmit={handleSwitchPlan}>
            <label>
              Organization
              <SearchableSelect
                options={organizationOptions}
                value={selectedOrg}
                onChange={setSelectedOrg}
                placeholder="Select organization..."
                searchPlaceholder="Search organizations..."
              />
            </label>
            <label>
              New Plan
              <SearchableSelect
                options={planOptions}
                value={switchPlanType}
                onChange={setSwitchPlanType}
                placeholder="Select plan..."
                searchPlaceholder="Search plans..."
              />
            </label>
            {switchPlanType === 'free_trial' && (
              <>
                <label>
                  Trial Days
                  <input 
                    type="number" 
                    min="1" 
                    value={switchTrialDays} 
                    onChange={(e) => setSwitchTrialDays(e.target.value)}
                    placeholder="Number of trial days"
                  />
                </label>
                <div style={{textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.5rem 0'}}>
                  —or—
                </div>
                <label>
                  Trial End Date
                  <input 
                    type="date" 
                    value={switchTrialEnd} 
                    onChange={(e) => setSwitchTrialEnd(e.target.value)} 
                  />
                </label>
              </>
            )}
            <button 
              className="primary" 
              type="submit" 
              disabled={buttonLoadingStates['switch-plan'] || !selectedOrg}
            >
              {buttonLoadingStates['switch-plan'] ? 'Updating Plan...' : 'Update Plan'}
            </button>
          </form>
        </details>

        {/* FILE OWNERSHIP TRANSFER */}
        <div className="card form-card fade-in">
          <h3>📁 Transfer File Ownership</h3>
          <div style={{
            padding: '0.75rem', 
            background: 'var(--warning-color)', 
            borderRadius: 'var(--radius)',
            fontSize: '0.85rem', 
            color: 'var(--text-primary)',
            marginBottom: '1rem'
          }}>
            <strong>⚠️ Important:</strong> This will change the user_id of an input file from one email to another.
            The file will be transferred from the source user to the destination user.
          </div>
          <form onSubmit={handleFileTransfer}>
            <label>
              File ID (UUID)
              <input
                type="text"
                value={fileTransferFileId}
                onChange={(e) => setFileTransferFileId(e.target.value)}
                placeholder="Enter the file ID (UUID) to transfer"
                required
                style={{fontFamily: 'monospace'}}
              />
            </label>
            <label>
              Destination User Email (New Owner)
              <input
                type="email"
                value={fileTransferNewEmail}
                onChange={(e) => setFileTransferNewEmail(e.target.value)}
                placeholder="Enter the new file owner's email"
                required
              />
            </label>
            <button 
              className="primary warning" 
              type="submit" 
              disabled={buttonLoadingStates['file-transfer'] || !fileTransferFileId || !fileTransferNewEmail}
            >
              {buttonLoadingStates['file-transfer'] ? 'Transferring File...' : 'Transfer File Ownership'}
            </button>
          </form>
        </div>

        {/* USER BLOCK/UNBLOCK MANAGEMENT */}
        <div className="card form-card fade-in" style={{ border: '2px solid var(--danger-color)' }}>
          <h3>⛔ Block / Unblock User</h3>
          <div style={{
            padding: '0.75rem', 
            background: '#fef2f2', 
            borderRadius: 'var(--radius)',
            fontSize: '0.85rem', 
            color: '#991b1b',
            marginBottom: '1rem'
          }}>
            <strong>🚫 Warning:</strong> Blocking a user will prevent them from logging in and using PolarCut entirely.
            All features will be disabled until they are unblocked.
          </div>
          
          <form onSubmit={(e) => e.preventDefault()}>
            <label>
              User Email
              <input
                type="email"
                value={blockUserEmail}
                onChange={(e) => {
                  setBlockUserEmail(e.target.value);
                  setUserBlockStatus(null);
                }}
                placeholder="Enter user email to block/unblock"
              />
            </label>
            
            {/* Check Status Button */}
            <button
              type="button"
              className="secondary"
              onClick={handleCheckBlockStatus}
              disabled={buttonLoadingStates['check-block-status'] || !blockUserEmail}
              style={{ marginBottom: '1rem', width: '100%' }}
            >
              {buttonLoadingStates['check-block-status'] ? 'Checking...' : '🔍 Check Block Status'}
            </button>
            
            {/* Status Display */}
            {userBlockStatus && (
              <div style={{
                padding: '0.75rem',
                background: userBlockStatus.isBlocked ? '#fef2f2' : '#f0fdf4',
                border: `1px solid ${userBlockStatus.isBlocked ? '#fecaca' : '#bbf7d0'}`,
                borderRadius: 'var(--radius)',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                <div style={{ 
                  fontWeight: '700', 
                  color: userBlockStatus.isBlocked ? '#dc2626' : '#16a34a',
                  marginBottom: '0.25rem'
                }}>
                  {userBlockStatus.isBlocked ? '⛔ USER IS BLOCKED' : '✅ USER IS ACTIVE'}
                </div>
                {userBlockStatus.isBlocked && userBlockStatus.reason && (
                  <div style={{ fontSize: '0.85rem', color: '#991b1b' }}>
                    <strong>Reason:</strong> {userBlockStatus.reason}
                  </div>
                )}
                {userBlockStatus.blockedAt && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    Blocked on: {new Date(userBlockStatus.blockedAt).toLocaleString()}
                  </div>
                )}
              </div>
            )}
            
            <label>
              Block Reason (optional)
              <input
                type="text"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Enter reason for blocking (e.g., 'Violation of terms')"
              />
            </label>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                className="danger"
                onClick={handleBlockUser}
                disabled={buttonLoadingStates['block-user'] || !blockUserEmail}
                style={{ flex: 1 }}
              >
                {buttonLoadingStates['block-user'] ? 'Blocking...' : '⛔ Block User'}
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleUnblockUser}
                disabled={buttonLoadingStates['unblock-user'] || !blockUserEmail}
                style={{ flex: 1, background: 'var(--success-color)' }}
              >
                {buttonLoadingStates['unblock-user'] ? 'Unblocking...' : '✅ Unblock User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreditManagement; 